import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function AboutSection() {
  const { t } = useTranslation();
  const { ref: ref1, isVisible: vis1 } = useScrollReveal(0.1);
  const { ref: ref2, isVisible: vis2 } = useScrollReveal(0.1);

  const features = [
    {
      icon: 'ri-leaf-line',
      titleKey: 'feat_sustainability',
      descKey: 'feat_sustainability_desc',
    },
    {
      icon: 'ri-shield-check-line',
      titleKey: 'feat_quality',
      descKey: 'feat_quality_desc',
    },
    {
      icon: 'ri-time-line',
      titleKey: 'feat_fresh',
      descKey: 'feat_fresh_desc',
    },
    {
      icon: 'ri-map-pin-line',
      titleKey: 'feat_origin',
      descKey: 'feat_origin_desc',
    },
  ];

  return (
    <section id="nosotros" className="py-16 md:py-24 bg-white">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div
            ref={ref1}
            className={`transition-all duration-700 text-center md:text-left ${
              vis1 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <span className="inline-block px-4 py-1.5 border border-brand-red/20 text-brand-red text-sm rounded-full mb-4">
              {t('about_badge')}
            </span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-brand-dark mb-6 leading-tight">
              {t('about_title')}{' '}
              <span className="text-brand-red">
                {t('about_title_highlight')}
              </span>
            </h2>
            <p className="text-brand-dark/60 text-base md:text-lg leading-relaxed mb-8 max-w-lg mx-auto md:mx-0">
              {t('about_desc')}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 md:gap-8 pt-8 border-t border-brand-dark/10">
              <div className="text-center md:text-left">
                <div className="text-2xl md:text-4xl font-bold text-brand-red">
                  8<span className="text-brand-gold">+</span>
                </div>
                <div className="text-brand-dark/50 text-xs md:text-sm mt-1">
                  {t('about_stat_suppliers')}
                </div>
              </div>
              <div className="text-center md:text-left">
                <div className="text-2xl md:text-4xl font-bold text-brand-red">
                  8<span className="text-brand-gold">+</span>
                </div>
                <div className="text-brand-dark/50 text-xs md:text-sm mt-1">
                  {t('about_stat_farms')}
                </div>
              </div>
              <div className="text-center md:text-left">
                <div className="text-2xl md:text-4xl font-bold text-brand-red">
                  25<span className="text-brand-gold">+</span>
                </div>
                <div className="text-brand-dark/50 text-xs md:text-sm mt-1">
                  {t('about_stat_accepted')}
                </div>
              </div>
            </div>
          </div>

          {/* Right Image */}
          <div
            ref={ref2}
            className={`relative transition-all duration-700 delay-200 ${
              vis2 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <div className="relative rounded-2xl overflow-hidden aspect-[4/5]">
              <img
                src="https://readdy.ai/api/search-image?query=master%20butcher%20artisan%20hand%20selecting%20premium%20beef%20cut%20dark%20professional%20kitchen%20warm%20lighting%20high%20quality%20photography%20dramatic%20contrast&width=600&height=750&seq=about-img&orientation=portrait"
                alt="Carnicero experto seleccionando cortes premium en L&J PREMIUM MEATS LLC, Washington Heights"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/40 to-transparent" />
            </div>

            {/* Floating card */}
            <div className="absolute -bottom-6 -left-6 md:-left-8 bg-white rounded-xl p-4 md:p-6 shadow-xl max-w-[220px] hover:shadow-2xl transition-shadow duration-300">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-brand-red/10 rounded-full flex items-center justify-center">
                  <i className="ri-award-line text-brand-red" />
                </div>
                <span className="font-display font-bold text-brand-dark">
                  {t('cert_title')}
                </span>
              </div>
              <p className="text-brand-dark/50 text-xs">{t('cert_desc')}</p>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mt-20">
          {features.map((feature, idx) => (
            <div
              key={idx}
              className="group p-5 md:p-6 rounded-xl bg-brand-cream hover:bg-brand-red transition-all duration-500 cursor-default hover:-translate-y-1"
              style={{ transitionDelay: `${idx * 100}ms` }}
            >
              <div className="w-12 h-12 bg-brand-red/10 group-hover:bg-white/20 rounded-lg flex items-center justify-center mb-4 transition-colors">
                <i
                  className={`${feature.icon} text-brand-red group-hover:text-white text-xl transition-colors`}
                />
              </div>
              <h3 className="font-display font-bold text-brand-dark group-hover:text-white text-lg mb-2 transition-colors">
                {t(feature.titleKey)}
              </h3>
              <p className="text-brand-dark/50 group-hover:text-white/70 text-sm leading-relaxed transition-colors">
                {t(feature.descKey)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}